import { useCallback } from 'react';
import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { useAppTakeoverSimpleHomePage } from 'components/app-takeover/use-app-takeover';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { t } from 'state/intl/translations';
import { AccessibleMainContent } from 'state/ui';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/types';
import { routes } from 'utils/routing';
import { isDelivery, isPickup } from 'utils/service-mode';

import { ICTAButtonProps, ICTAButtonsProps } from '../types';

const CTAButton = ({
  children,
  iconVariant,
  defaultLinkTo,
  variant,
  isMatchingServiceMode,
  numberOfItemsInCart,
  marginTop,
  eventName,
  itemId,
  ...rest
}: ICTAButtonProps) => {
  const { noStoreSelected } = useStoreContext();
  const { navigate } = useRouter();
  const shouldGoToCart = isMatchingServiceMode && numberOfItemsInCart > 0;
  const shouldGoToMenu = isMatchingServiceMode && !noStoreSelected;

  const { openSignInModal, isAuthenticated } = useAuthContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const { logExposure } = useExperiments();

  const handleOnPress = useCallback(() => {
    let path: string = defaultLinkTo;
    path = shouldGoToMenu ? routes.menu : path;
    path = shouldGoToCart ? routes.cart : path;

    if (shouldGoToCart) {
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    }

    if (shouldGoToCart && !isAuthenticated) {
      logExposure(RunningExperiments.SignInUpAfterCart);

      if (!isSignUpAfterCartEnabled) {
        openSignInModal({
          navigateOnSuccessRoute: routes.cart,
        });

        return;
      }
    }

    logEvent(eventName, EventTypes.Navigation, {
      'Hero URL': path,
      'Hero ID': itemId,
    });

    if (path === routes.cart) {
      startTrace(TraceName.CART_LOAD_TO_CONTINUE_LOADED);
    }
    if (path === routes.menu) {
      startTrace(TraceName.MENU_LOAD);
    }

    return navigate(path);
  }, [
    isAuthenticated,
    defaultLinkTo,
    eventName,
    itemId,
    navigate,
    shouldGoToMenu,
    shouldGoToCart,
    isSignUpAfterCartEnabled,
    openSignInModal,
    logExposure,
  ]);

  const continueToCartText = shouldGoToCart
    ? t('continueToCart', { numberOfItemsInCart })
    : children;

  return (
    <ActionButton
      minHeight={50}
      variant={variant}
      eventAttributes={{ Name: shouldGoToCart ? continueToCartText : children }}
      leftIcon={<Icon variant={iconVariant} />}
      onPress={handleOnPress}
      marginTop={marginTop}
      accessibilityLabel={children}
      {...rest}
    >
      {continueToCartText}
    </ActionButton>
  );
};

export const CTAButtons = ({ numberOfItemsInCart, serviceMode, itemId }: ICTAButtonsProps) => {
  const { isTakeoverActive, deliveryButton, primaryButton } = useAppTakeoverSimpleHomePage(itemId);

  return (
    <AccessibleMainContent>
      <CTAButton
        variant={
          isTakeoverActive ? ActionButtonVariants.PRIMARY_TAKEOVER : ActionButtonVariants.PRIMARY
        }
        defaultLinkTo={
          serviceMode ? `${routes.storeLocator}?redirectUrl=${routes.menu}` : routes.menu
        }
        iconVariant="cart"
        isMatchingServiceMode={isPickup(serviceMode)}
        numberOfItemsInCart={numberOfItemsInCart}
        eventName={CustomEventNames.HERO_PRIMARY_CTA_CLICK}
        itemId={itemId}
        {...primaryButton}
      >
        {t('orderPickup')}
      </CTAButton>
      <CTAButton
        variant={ActionButtonVariants.OUTLINE}
        defaultLinkTo={routes.deliveryAddress}
        iconVariant="delivery"
        isMatchingServiceMode={isDelivery(serviceMode)}
        numberOfItemsInCart={numberOfItemsInCart}
        marginTop="$2"
        eventName={CustomEventNames.HERO_COMPLEMENTARY_CTA_CLICK}
        itemId={itemId}
        {...deliveryButton}
      >
        {t('orderDelivery')}
      </CTAButton>
    </AccessibleMainContent>
  );
};
